import React from 'react';
import { Card, Skeleton, SkeletonAvatar, SkeletonButton } from '@parsina/framework';
import { C, CT, GRADIENT_BRAND } from '../../constants/theme';
import { SkeletonPage } from '../../components/common/skeletons.jsx';

// امروز — the loading shape of the day sheet, desktop and mobile.
//
// The page chrome (header, date navigator, KPI tiles) is REAL while the day
// loads — `KpiCard`/`StatTiles` carry their own `loading` dash — so what
// skeletons here is only the part the query fills: the next-patient hero and
// the first block of appointment rows. Geometry follows NextPatientHero,
// AppointmentRow (desktop) and MobileNextPatient, MobileAppointmentCard
// (mobile) exactly, so the rows the dentist is about to click do not move when
// the data lands.

// ── desktop ─────────────────────────────────────────────────────────────────

/** NextPatientHero: gradient band, 56px avatar, kicker + 22px name, two buttons. */
const HeroSkeleton = () => (
  <div
    aria-hidden
    style={{
      background: GRADIENT_BRAND, borderRadius: 16, padding: '20px 24px', marginBottom: 18,
      display: 'flex', alignItems: 'center', gap: 18,
    }}
  >
    <SkeletonAvatar size={56} tone="onDark" />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Skeleton width={96} height={12} tone="onDark" />
      <Skeleton width="40%" height={22} tone="onDark" />
      <Skeleton width={120} height={12} tone="onDark" />
    </div>
    <div style={{ display: 'flex', gap: 10 }}>
      <SkeletonButton width={88} tone="onDark" />
      <SkeletonButton width={88} tone="onDark" />
    </div>
  </div>
);

/** AppointmentRow: dot · time · divider · 32px avatar · name/phone · pill · two sm buttons. */
const RowSkeleton = ({ i }) => (
  <div
    aria-hidden
    style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      borderRadius: 12, marginBottom: 8, background: CT.surface,
      border: `1.5px solid ${CT.surface2}`,
    }}
  >
    <Skeleton width={8} circle />
    <Skeleton width={44} height={14} />
    <div style={{ width: 1, height: 28, background: C.border, flexShrink: 0 }} />
    <SkeletonAvatar size={32} />
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 7 }}>
      <Skeleton width={`${22 + ((i * 9) % 16)}%`} height={13} />
      <Skeleton width={78} height={11} />
    </div>
    <Skeleton width={64} height={22} radius={999} />
    <div style={{ display: 'flex', gap: 6 }}>
      <SkeletonButton size="sm" width={64} />
      <SkeletonButton size="sm" width={64} />
    </div>
  </div>
);

/** A day-sheet Block: titled strip + count pill over its rows. */
const BlockSkeleton = ({ rows }) => (
  <Card style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }} aria-hidden>
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px',
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <Skeleton width={96} height={14} />
      <Skeleton width={28} height={20} radius={999} />
    </div>
    <div style={{ padding: 12 }}>
      {Array.from({ length: rows }, (_, i) => <RowSkeleton key={i} i={i} />)}
    </div>
  </Card>
);

export const TodaySkeletonWeb = ({ label }) => (
  <SkeletonPage label={label} data-testid="today-skeleton">
    <HeroSkeleton />
    <BlockSkeleton rows={3} />
    <BlockSkeleton rows={2} />
  </SkeletonPage>
);

// ── mobile ──────────────────────────────────────────────────────────────────

/** MobileNextPatient: 40px avatar, kicker/name/time, full-width ghost button. */
const MobileHeroSkeleton = () => (
  <div
    aria-hidden
    style={{ background: GRADIENT_BRAND, borderRadius: '1rem', padding: '0.875rem' }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
      <SkeletonAvatar size={40} tone="onDark" />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Skeleton width={72} height={10} tone="onDark" />
        <Skeleton width="55%" height={15} tone="onDark" />
        <Skeleton width={90} height={10} tone="onDark" />
      </div>
      <Skeleton width={56} height={22} radius={999} tone="onDark" />
    </div>
    <Skeleton width="100%" height={44} radius={10} tone="onDark" style={{ marginTop: '0.75rem' }} />
  </div>
);

/** MobileAppointmentCard: time · 32px avatar · name/phone · status pill. */
const MobileCardSkeleton = ({ i }) => (
  <Card aria-hidden style={{ padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.875rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
      <Skeleton width={44} height={13} />
      <SkeletonAvatar size={32} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Skeleton width={`${45 + ((i * 13) % 30)}%`} height={13} />
        <Skeleton width="35%" height={11} />
      </div>
      <Skeleton width={56} height={22} radius={999} />
    </div>
  </Card>
);

export const TodaySkeletonMobile = ({ label }) => (
  <SkeletonPage label={label} data-testid="today-skeleton">
    <MobileHeroSkeleton />
    <div style={{ marginTop: '0.875rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.5rem', minHeight: 28 }} aria-hidden>
        <Skeleton width={96} height={13} />
        <Skeleton width={24} height={18} radius={999} />
      </div>
      {[0, 1, 2, 3].map((i) => <MobileCardSkeleton key={i} i={i} />)}
    </div>
  </SkeletonPage>
);
